<template>
  <BContainer
    v-if="currentUser"
    class="mb-4"
  >

    <div class="d-flex justify-content-end">
      <BBreadcrumb
        v-if="$route.meta.breadcrumb"
        :items="$route.meta.breadcrumb || []"
      />
    </div>

    <BRow
      class="py-2"
    >
      <BCol
        cols="12"
        md="8"
      >
        <BFormGroup
          label="Search"
          label-for="filterSearch"
          label-class="font-weight-bolder"
          class="mb-2"
        >
          <BFormInput
            id="filterSearch"
            v-model="tableMunicipalities.filter.search"
            placeholder="search here"
            autocomplete="off"
            debounce="1000"
            type="text"
          />
        </BFormGroup>
      </BCol>
      <BCol
        cols="12"
        md="4"
      >
        <BFormGroup
          label="Province"
          label-for="filterProvince"
          label-class="font-weight-bolder"
          class="mb-2"
        >
          <VSelect
            v-model="tableMunicipalities.selected.province"
            placeholder="filter province here"
            input-id="filterProvince"
            class="vs-white"
            label="province_name"
            clearable
            append-to-body
            :options="(tableMunicipalities.options.provinces)"
            :loading="(tableMunicipalities.fetching.provinces)"
            :disabled="(tableMunicipalities.busy || tableMunicipalities.fetching.provinces)"
            :calculate-position="calculateDropPosition"
            @search="searchFilterProvinces"
          >
            <template #option="{ province_name, region }">
              <div
                class="text-break text-wrap"
                v-text="province_name"
              />
              <div
                v-if="region"
                class="text-muted small"
                v-text="region?.region_name"
              />
            </template>
            <template #selected-option="{ province_name }">
              <div
                class="w-225px w-md-100px w-lg-200px text-truncate"
                v-text="province_name"
              />
            </template>
            <template #no-options="">
              no available province
            </template>
          </VSelect>
        </BFormGroup>
      </BCol>
    </BRow>

    <CustomTable
      ref="tableMunicipalities"
      :provider="tableProvider"
      :fields="tableMunicipalities.fields"
      :filter="tableMunicipalities.filter"
      :filter-on="tableMunicipalities.filterOn"
      :sort-by="tableMunicipalities.sortBy"
      :sort-desc="tableMunicipalities.sortDesc"
      :sort-direction="tableMunicipalities.sortDirection"
      :total-rows="tableMunicipalities.totalRows"
      :current-page="tableMunicipalities.currentPage"
      :per-page="tableMunicipalities.perPage"
      :stacked="tableMunicipalities.stacked"
      :busy="tableMunicipalities.busy"
    >

      <template #cell(action)="{ item }">
        <div class="text-md-nowrap d-flex flex-column flex-md-row justify-content-center">
          <BButton
            size="sm"
            :disabled="tableMunicipalities.busy"
            :variant="Number(item.active) === 1 ? 'danger' : 'primary'"
            @click.prevent="updateMunicipality(item)"
          >
            {{ Number(item.active) === 1 ? 'Deactivate' : 'Activate' }}
          </BButton>
        </div>
      </template>
    </CustomTable>

    <BModal
      ref="modalMunicipality"
      scrollable
      no-close-on-esc
      no-enforce-focus
      no-close-on-backdrop
      aria-hidden="false"
      role="dialog"
      :title="MMunicipalityTitle"
    >
      <ValidationObserver
        ref="formMunicipality"
      >
        <form
          role="form"
          novalidate
          @submit.prevent
        >
          <BRow>
            <BCol
              cols="12"
            >
              <ValidationProvider
                v-slot="{ errors }"
                name="municipality name"
                vid="municipality_name"
                rules="required|max:150"
              >
                <BFormGroup
                  label="Municipality Name"
                  label-for="fMunicipalityName"
                  label-class="font-weight-bolder"
                  class="mb-2"
                >
                  <BFormInput
                    id="fMunicipalityName"
                    v-model="formMunicipality.municipality_name"
                    type="text"
                    maxlength="150"
                    autocomplete="off"
                    placeholder="enter municipality name here"
                    :state="errors.length > 0 ? false : null"
                  />
                  <div
                    v-if="(errors.length > 0)"
                    class="invalid-feedback"
                    v-text="errors[0]"
                  />
                </BFormGroup>
              </ValidationProvider>
            </BCol>

            <BCol
              cols="12"
            >
              <ValidationProvider
                v-slot="{ errors }"
                vid="province"
                name="province"
                rules="required"
              >
                <BFormGroup
                  label="Province"
                  label-for="fProvince"
                  label-class="font-weight-bolder"
                  class="mb-2"
                >
                  <VSelect
                    v-model="selected.province"
                    input-id="fProvince"
                    label="province_name"
                    append-to-body
                    placeholder="search province here"
                    :options="options.provinces"
                    :disabled="fetching.provinces"
                    :loading="fetching.provinces"
                    :calculate-position="calculateDropPosition"
                    :class="{ 'is-invalid': errors.length > 0 }"
                    @search="searchProvinces"
                  >
                    <template #no-options="">
                      no available province
                    </template>
                  </VSelect>
                  <div
                    v-if="(errors.length > 0)"
                    class="invalid-feedback"
                    v-text="errors[0]"
                  />
                </BFormGroup>
              </ValidationProvider>
            </BCol>

            <BCol
              v-if="state.editing"
              cols="12"
            >
              <BFormGroup>
                <BFormCheckbox
                  id="fActive"
                  v-model="formMunicipality.active"
                  switch
                  value="1"
                  name="active"
                  unchecked-value="0"
                  :disabled="state.busy"
                >
                  Is Active?
                </BFormCheckbox>
              </BFormGroup>
            </BCol>
          </BRow>

        </form>
      </ValidationObserver>

      <template #modal-footer="{cancel}">
        <BButton
          variant="primary"
          :disabled="state.busy"
          @click.prevent="validateMunicipality"
        >
          {{ state.editing ? 'Update Municipality' : 'Create Municipality' }}
        </BButton>
        <BButton
          variant="outline-dark"
          :disabled="state.busy"
          @click.prevent="discardBtnEvent => preventAriaHidden(discardBtnEvent, cancel)"
        >
          Discard
        </BButton>
      </template>

    </BModal>
  </BContainer>
</template>

<script>
import { mapGetters } from 'vuex'
import QUERY from 'lodash'

import MxMisc from '@/mixins/misc'
import MxFormatter from '@/mixins/formatter'
import MProvinces from '@/mixins/locations/provinces'

import { SAdminMunicipality } from '@/services/admin'

export default {
  name: 'VAdminMunicipalitys',

  mixins: [
    MProvinces,
    MxMisc,
    MxFormatter
  ],

  middleware: [
    'auth',
    'admin'
  ],

  metaInfo: () => ({
    title: 'Municipalities'
  }),

  data () {
    return {
      state: {
        busy: false,
        editing: false
      },
      formMunicipality: {
        id: '',
        province: '',
        municipality_name: '',
        active: 1
      },
      tableMunicipalities: {
        busy: false,
        fetching: {
          provinces: false
        },
        options: {
          provinces: []
        },
        selected: {
          province: ''
        },
        filter: {
          search: '',
          province: ''
        },
        filterOn: [],
        stacked: 'sm',
        sortBy: 'id',
        sortDesc: false,
        sortDirection: 'asc',
        pageOptions: [10, 25, 50, 100],
        currentPage: 1,
        totalRows: 0,
        perPage: 10,
        fields: [
          { mobile: 0, key: 'index', label: '#', class: 'text-center' },
          { mobile: 5, key: 'action', class: 'text-center w-md-125px' },
          { mobile: 1, key: 'municipality_name', class: 'min-w-175px' },
          { mobile: 2, key: 'province.province_name', label: 'Province', class: 'min-w-175px' },
          { mobile: 3, key: 'active', class: 'text-md-center w-md-75px', formatter: this.yesOrNo },
          { mobile: 4, key: 'updated_at', class: 'min-w-150px', formatter: this.dateTimeShortFormatter, sortable: true }
        ].sort(this.scaleOnMobile)
      }
    }
  },

  computed: Object.assign({
    MMunicipalityTitle () {
      return this.state.editing ? 'Edit Municipality' : 'Create Municipality'
    }
  }, mapGetters({
    currentUser: 'auth/user'
  })),

  watch: {
    'selected.province' (province) {
      this.formMunicipality.province = province?.id || ''
    },
    'tableMunicipalities.selected.province' (province) {
      this.tableMunicipalities.filter.province = province?.id || ''
    }
  },

  mounted () {
    if (this.currentUser) {
      this.getProvinces(this.tableMunicipalities)
    }
  },

  methods: {

    async tableProvider ({ currentPage, perPage, sortBy, sortDesc, filter }) {
      this.tableMunicipalities.busy = true
      const payload = {
        page: currentPage,
        per_page: perPage,
        sort: sortBy,
        sort_desc: sortDesc,
        filter_text: filter.search,
        province: filter.province
      }

      return await SAdminMunicipality.get(payload).then(
        ({ data }) => {
          this.tableMunicipalities.totalRows = data.total_rows
          return data.items
        }
      ).catch(() => {
        this.tableMunicipalities.totalRows = 0
        return []
      }).finally(() => {
        this.tableMunicipalities.busy = false
      })
    },

    async searchFilterProvinces (search, loading) {
      this.searchProvincesProvider(this.tableMunicipalities, search, loading)
    },

    createMunicipality () {
      this.state.editing = false
      this.formMunicipality = Object.assign(this.formMunicipality, {
        id: '',
        municipality_name: '',
        active: 1
      })

      this.getProvinces(this)

      this.$refs.modalMunicipality.show()
    },

    editMunicipality (municipality) {
      this.state.editing = true
      this.formMunicipality = Object.assign(this.formMunicipality, {
        id: municipality.id,
        province: municipality.province_id,
        municipality_name: municipality.municipality_name,
        active: municipality.active
      })

      this.getProvinces(this).finally(() => {
        this.selected.province = QUERY.find(this.options.provinces, {
          id: municipality.province_id
        })
      })

      this.$refs.modalMunicipality.show()
    },

    async updateMunicipality (municipality) {
      const isActive = Number(municipality.active) === 1

      this.formMunicipality = Object.assign(this.formMunicipality, {
        id: municipality.id,
        province: municipality.province_id,
        municipality_name: municipality.municipality_name,
        active: isActive ? 0 : 1
      })

      this.swalConfirm({
        html: isActive ? '<h6>Deactivate this Municipality?</h6>' : '<h6>Activate this Municipality?</h6>',
        confirmButtonText: isActive ? 'Deactivate' : 'Activate',
        customClass: {
          confirmButton: isActive ? 'btn btn-sm btn-danger' : 'btn btn-sm btn-primary'
        },
        preConfirm: async () => {
          this.state.busy = true
          return this.putMunicipality().finally(() => {
            this.state.busy = false
          })
        }
      })
    },

    async validateMunicipality () {
      await this.$refs.formMunicipality.validate().then(
        async validMunicipality => {
          if (!validMunicipality) {
            return this.swalInvalid()
          }

          this.swalConfirm({
            html: this.state.editing ? '<h6>Update this Municipality?</h6>' : '<h6>Create this Municipality?</h6>',
            confirmButtonText: this.state.editing ? 'Update' : 'Create',
            preConfirm: async () => {
              this.state.busy = true

              const createOrUpdate = async () => {
                return this.state.editing ? await this.putMunicipality() : await this.postMunicipality()
              }

              return createOrUpdate().catch(
                ({ message }) => {
                  this.$refs.formMunicipality.setErrors(message.errors)
                }
              ).finally(() => {
                this.state.busy = false
              })
            }
          })
        }
      )
    },

    async postMunicipality () {
      return await SAdminMunicipality.post(this.formMunicipality).then(
        ({ data: { message } }) => {
          this.swalSuccess(message).then(() => {
            this.$refs.modalMunicipality.hide()
            this.$refs.tableMunicipalities.refetch()
          })
        }
      )
    },

    async putMunicipality () {
      return await SAdminMunicipality.put(this.formMunicipality).then(
        ({ data: { message, municipality } }) => {
          this.swalSuccess(message).then(() => {
            this.$refs.modalMunicipality.hide()
            this.$refs.tableMunicipalities.updateRow({
              id: municipality.id,
              province: municipality.province,
              province_id: municipality.province_id,
              municipality_name: municipality.municipality_name,
              active: municipality.active,
              updated_at: municipality.updated_at
            })
          })
        }
      )
    }
  }
}
</script>
